import { useCallback, useEffect, useRef } from "react";
import type { RefCallback } from "react";

/**
 * ESCAPE-TO-CLOSE for overlays — the one place the whole modal set gets its keyboard dismiss.
 *
 * Every `src/ui/*Modal.tsx` closed on click-outside and nothing else, so the key every other interface
 * teaches a player did nothing. The listener pattern here is the one already used by BarMenu, HudMenu,
 * LevelPanel and CelebrationModals — a `keydown` on `window` — not a new mechanism.
 *
 * WHY A STACK RATHER THAN A LISTENER PER MODAL. A plain global listener in each overlay fires in ALL of them
 * at once, so with a modal and its inner popup open Escape would close both and the player would lose the
 * whole screen instead of backing out one step. Layers register here on mount, and only the TOP of the stack
 * handles a press. That is what makes "close the top one only" fall out for free, and it is why this is one
 * shared module holding a single listener rather than 24 copies of an effect.
 *
 * ORDERING. Effects run child-first, but a nested popup is mounted LATER than the modal containing it (it
 * appears when the player opens it), so it registers later and sits on top — which is the behaviour we want.
 * A layer is removed by IDENTITY, so closing overlays out of order can never pop the wrong one.
 *
 * UNSAVED EDITS. Pass `enabled: false` to opt a layer out while it holds work a player would lose. Escape
 * then falls through to the next layer down, which is deliberate: a modal that refuses to close should not
 * also swallow the key. `SignEditModal` is the live case — see its call site.
 *
 * The close callback is read through a ref, so a modal passing an inline arrow (all of them do) does not
 * re-register on every render — only mount/unmount and `enabled` touch the stack.
 *
 * FOCUS. The hook also RETURNS a ref for the overlay's `role="dialog"` element; attaching it makes the dialog
 * behave as a modal for keyboard users — see `useDialogFocus` below. It is a ref rather than a DOM lookup
 * because the same stack also carries layers that are not dialogs at all (the rod, the bug net, a placement
 * ghost, photo mode) and those must never trap anything. Escape handling does not depend on the ref.
 */

interface Layer { close: () => void; enabled: boolean }

/** Mount-ordered stack of dismissable layers. The topmost ENABLED entry handles a press. */
const stack: Layer[] = [];
let listening = false;

function onKeyDown(e: KeyboardEvent): void {
  if (e.key !== "Escape") return;
  // Walk from the top down to the first layer that will take it. A disabled layer (unsaved edits) is skipped
  // rather than blocking, so Escape still backs out of whatever sits beneath it.
  for (let i = stack.length - 1; i >= 0; i--) {
    const layer = stack[i]!;
    if (!layer.enabled) continue;
    e.preventDefault();
    e.stopPropagation();
    layer.close();
    return;
  }
}

function attach(): void {
  if (listening) return;
  window.addEventListener("keydown", onKeyDown);
  listening = true;
}
function detachIfIdle(): void {
  if (!listening || stack.length) return;
  window.removeEventListener("keydown", onKeyDown);
  listening = false;
}

/**
 * Close this overlay when Escape is pressed, provided it is the topmost open one.
 *
 * @param onClose  called on Escape. Should do exactly what the ✕ button does.
 * @param enabled  default true. Pass false to decline the key (e.g. unsaved edits) — the press then goes to
 *                 the layer below rather than being swallowed.
 */
export function useEscapeClose(onClose: () => void, enabled = true): RefCallback<HTMLElement> {
  // Latest callback without re-registering: the modals all pass inline arrows, which change every render.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!enabled) return;
    const layer: Layer = { close: () => closeRef.current(), enabled: true };
    stack.push(layer);
    attach();
    return () => {
      const i = stack.indexOf(layer);   // by identity: an out-of-order close must not pop the wrong layer
      if (i >= 0) stack.splice(i, 1);
      detachIfIdle();
    };
  }, [enabled]);

  return useDialogFocus();
}

/**
 * Component form of the hook, for an overlay rendered INLINE rather than as its own component — a popup
 * built from an IIFE inside a parent's JSX can't call a hook, but it can render this. Drop it inside the
 * popup: it registers a layer for as long as the popup is mounted, and unregisters when it closes.
 * Renders nothing.
 */
export function EscapeLayer({ onClose, enabled = true }: { onClose: () => void; enabled?: boolean }): null {
  useEscapeClose(onClose, enabled);
  return null;
}

/**
 * MODAL FOCUS — what a keyboard player needs from an overlay, applied to whatever element the returned ref is
 * attached to (the `role="dialog"` box, not the backdrop):
 *
 *  - `aria-modal="true"`, so assistive tech treats the game behind as inert;
 *  - on open, focus moves INTO the dialog: kept where it is if something inside already took it (an
 *    `autoFocus` input — React focuses children before the parent's ref attaches), else the first focusable
 *    control that is not a text field, else the dialog box itself. Text fields are skipped on purpose:
 *    focusing one on open raises the on-screen keyboard on a phone for a player who only wanted to look;
 *  - Tab and Shift+Tab wrap within the TOPMOST dialog instead of walking out into the HUD and the game;
 *  - on close, focus goes back to whatever held it when the dialog opened (the button that opened it).
 *
 * Trapping is independent of Escape's `enabled`: a modal holding unsaved edits declines Escape but is still
 * a modal. Dialogs stack the same way escape layers do — attached later means on top — and are removed by
 * identity. The returned callback is stable, so React attaches it once per mount rather than every render.
 */
export function useDialogFocus(): RefCallback<HTMLElement> {
  const trapRef = useRef<Trap | null>(null);
  return useCallback((el: HTMLElement | null) => {
    if (el) {
      if (trapRef.current) releaseTrap(trapRef.current);
      trapRef.current = holdTrap(el);
    } else if (trapRef.current) {
      releaseTrap(trapRef.current);
      trapRef.current = null;
    }
  }, []);
}

interface Trap { el: HTMLElement; restore: HTMLElement | null }

/** Attach-ordered stack of open modal dialogs. Only the TOP one confines Tab. */
const traps: Trap[] = [];

const FOCUSABLE = [
  "a[href]", "button:not([disabled])", "input:not([disabled]):not([type='hidden'])", "select:not([disabled])",
  "textarea:not([disabled])", "[tabindex]:not([tabindex='-1'])", "[contenteditable='true']",
].join(",");

/** Focusable, rendered (not display:none / inside a collapsed section) descendants, in tab order. */
function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.getClientRects().length > 0);
}

function isTextEntry(n: HTMLElement): boolean {
  if (n.isContentEditable || n.tagName === "TEXTAREA" || n.tagName === "SELECT") return true;
  if (n.tagName !== "INPUT") return false;
  const t = (n as HTMLInputElement).type;
  return !["button", "submit", "reset", "checkbox", "radio", "range", "color", "file", "image"].includes(t);
}

function focusBox(el: HTMLElement): void {
  if (!el.hasAttribute("tabindex")) el.tabIndex = -1;   // programmatically focusable, never a Tab stop
  el.focus({ preventScroll: true });
}

function holdTrap(el: HTMLElement): Trap {
  const prev = document.activeElement;
  const trap: Trap = { el, restore: prev instanceof HTMLElement && prev !== document.body && !el.contains(prev) ? prev : null };
  el.setAttribute("aria-modal", "true");
  traps.push(trap);
  if (traps.length === 1) document.addEventListener("keydown", onTabKey);
  if (!el.contains(document.activeElement)) {
    const first = focusables(el).find((n) => !isTextEntry(n));
    if (first) first.focus({ preventScroll: true }); else focusBox(el);
  }
  return trap;
}

function releaseTrap(trap: Trap): void {
  const i = traps.indexOf(trap);   // by identity, like the escape stack
  if (i < 0) return;
  traps.splice(i, 1);
  if (!traps.length) document.removeEventListener("keydown", onTabKey);
  // Hand focus back only if it is still ours to hand: it was inside this dialog (or fell to <body> as the dialog
  // unmounted), and the opener is still on the page and not shut out by another dialog left open on top.
  const { restore } = trap;
  const active = document.activeElement;
  const lost = !active || active === document.body || trap.el.contains(active);
  const top = traps[traps.length - 1];
  if (restore && lost && restore.isConnected && (!top || top.el.contains(restore))) restore.focus({ preventScroll: true });
}

function onTabKey(e: KeyboardEvent): void {
  if (e.key !== "Tab" || e.defaultPrevented) return;
  const top = traps[traps.length - 1];
  if (!top) return;
  const items = focusables(top.el);
  const active = document.activeElement;
  if (!items.length) { e.preventDefault(); focusBox(top.el); return; }
  const first = items[0]!, last = items[items.length - 1]!;
  if (!(active instanceof HTMLElement) || !top.el.contains(active)) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  } else if (e.shiftKey && (active === first || active === top.el)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

/** Test seam: how many layers are currently registered (0 when every overlay is closed). */
export function escapeLayerCount(): number { return stack.length; }
